import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    subject: '',
    message: '',
    preferredContact: '',
    newsletter: false,
    urgency: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const inquiryTypes = [
    { value: 'general', label: 'General Questions', description: 'Basic information about Cynizee' },
    { value: 'partnership', label: 'Partnership Opportunities', description: 'Business collaboration inquiries' },
    { value: 'accessibility', label: 'Accessibility Support', description: 'Special needs and accessibility features' },
    { value: 'technical', label: 'Technical Issues', description: 'Bug reports and technical problems' },
    { value: 'media', label: 'Media & Press', description: 'Press inquiries and media requests' },
    { value: 'educator', label: 'Educator Resources', description: 'Resources for teachers and therapists' }
  ];

  const contactPreferences = [
    { value: 'email', label: 'Email (Preferred)', description: 'Response within 24 hours' },
    { value: 'phone', label: 'Phone Call', description: 'Response within 2 business days' },
    { value: 'either', label: 'Either Method', description: 'Fastest available response' }
  ];

  const urgencyLevels = [
    { value: 'low', label: 'Low Priority', description: 'Response within 3-5 business days' },
    { value: 'medium', label: 'Medium Priority', description: 'Response within 1-2 business days' },
    { value: 'high', label: 'High Priority', description: 'Response within 24 hours' },
    { value: 'urgent', label: 'Urgent', description: 'Response within 4 hours (business hours only)' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData?.name?.trim()) newErrors.name = 'Name is required';
      if (!formData?.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData?.inquiryType) newErrors.inquiryType = 'Please select an inquiry type';
    }
    
    if (step === 2) {
      if (!formData?.subject?.trim()) newErrors.subject = 'Subject is required';
      if (!formData?.message?.trim()) {
        newErrors.message = 'Message is required';
      } else if (formData?.message?.trim()?.length < 10) {
        newErrors.message = 'Message must be at least 10 characters long';
      }
      if (!formData?.preferredContact) newErrors.preferredContact = 'Please select a contact preference';
      if (!formData?.urgency) newErrors.urgency = 'Please select urgency level';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateStep(2)) return;
    
    setIsSubmitting(true);
    
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful submission
      console.log('Form submitted:', formData);
      setIsSubmitted(true);
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          inquiryType: '',
          subject: '',
          message: '',
          preferredContact: '',
          newsletter: false,
          urgency: ''
        });
        setCurrentStep(1);
        setIsSubmitted(false);
      }, 3000);
      
    } catch (error) {
      setErrors({ submit: 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-card rounded-2xl p-8 shadow-soft border border-border text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircle" size={32} className="text-success" />
        </div>
        <h3 className="text-2xl font-heading font-bold text-foreground mb-4">
          Message Sent Successfully!
        </h3>
        <p className="text-text-secondary mb-6">
          Thank you for reaching out to us. We've received your message and will get back to you within the timeframe you selected. Check your email for a confirmation.
        </p>
        <div className="bg-muted rounded-lg p-4">
          <p className="text-sm text-foreground">
            <strong>What's next?</strong> Our team will review your inquiry and respond via your preferred contact method. For urgent matters, we'll prioritize accordingly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl shadow-soft border border-border overflow-hidden">
      {/* Progress Indicator */}
      <div className="bg-muted px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
              currentStep >= 1 ? 'bg-primary text-primary-foreground' : 'bg-border text-muted-foreground'
            }`}>
              1
            </div>
            <div className={`w-16 h-1 rounded-full ${
              currentStep >= 2 ? 'bg-primary' : 'bg-border'
            }`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
              currentStep >= 2 ? 'bg-primary text-primary-foreground' : 'bg-border text-muted-foreground'
            }`}>
              2
            </div>
          </div>
          <div className="text-sm text-text-secondary">
            Step {currentStep} of 2
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="p-8">
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                Let's Get Started
              </h3>
              <p className="text-text-secondary">
                Tell us a bit about yourself and what you need help with.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                placeholder="Enter your full name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />

              <Input
                label="Email Address"
                type="email"
                placeholder="your.email@example.com"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                description="We'll send confirmation to this email"
                required
              />
            </div>

            <Input
              label="Phone Number (Optional)"
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              description="Only if you prefer phone contact"
            />

            <Select
              label="What can we help you with?"
              placeholder="Select inquiry type"
              options={inquiryTypes}
              value={formData?.inquiryType}
              onChange={(value) => handleInputChange('inquiryType', value)}
              error={errors?.inquiryType}
              required
              searchable
            />

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleNext}
                variant="default"
                iconName="ArrowRight"
                iconPosition="right"
                className="bg-accent hover:bg-accent/90"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                  Your Message
                </h3>
                <p className="text-text-secondary">
                  Provide details about your inquiry so we can help you better.
                </p>
              </div>
              <Button
                type="button"
                onClick={handleBack}
                variant="ghost"
                iconName="ArrowLeft"
                iconPosition="left"
                size="sm"
              >
                Back
              </Button>
            </div>

            <Input
              label="Subject"
              type="text"
              placeholder="Brief description of your inquiry"
              value={formData?.subject}
              onChange={(e) => handleInputChange('subject', e?.target?.value)}
              error={errors?.subject}
              required
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Message <span className="text-destructive">*</span>
              </label>
              <textarea
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent resize-none transition-colors duration-200"
                rows={6}
                placeholder="Please provide as much detail as possible about your inquiry..."
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
              />
              {errors?.message && (
                <p className="mt-1 text-sm text-destructive">{errors?.message}</p>
              )}
              <p className="mt-1 text-xs text-text-secondary">
                {formData?.message?.length}/500 characters
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Preferred Contact Method"
                placeholder="How should we reach you?"
                options={contactPreferences}
                value={formData?.preferredContact}
                onChange={(value) => handleInputChange('preferredContact', value)}
                error={errors?.preferredContact}
                required
              />

              <Select
                label="Urgency Level"
                placeholder="How urgent is this?"
                options={urgencyLevels}
                value={formData?.urgency}
                onChange={(value) => handleInputChange('urgency', value)}
                error={errors?.urgency}
                required
              />
            </div>

            <div className="bg-muted rounded-lg p-4">
              <Checkbox
                label="Subscribe to Cynizee updates"
                description="Get notified about new features, family organization tips, and early access opportunities"
                checked={formData?.newsletter}
                onChange={(e) => handleInputChange('newsletter', e?.target?.checked)}
              />
            </div>

            {errors?.submit && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-destructive" />
                  <p className="text-sm text-destructive">{errors?.submit}</p>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="default"
                loading={isSubmitting}
                iconName="Send"
                iconPosition="right"
                className="bg-accent hover:bg-accent/90"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;